import React, { useState } from 'react';
import {
  Dialog,
  Button,
  FormControl,
  DialogTitle,
  DialogActions,
  IconButton,
  Typography,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Close from '@mui/icons-material/Close';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCancelInspectionMutation } from 'redux/services/inspection';

import './DeleteInspectionModal.scss';

type DeleteInspectionModalProps = {
  inspectionId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export const DeleteInspectionModal: React.FC<DeleteInspectionModalProps> = ({
  inspectionId,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const { projectId } = useParams();
  const [cancelInspection] = useCancelInspectionMutation();

  const handleSubmit = async () => {
    const res: any = await cancelInspection({
      id: inspectionId,
      projectId,
      body: {
        cancelReason: reason,
      },
    });

    if (res.data) {
      toast.success(t('success_inspection_cancelled'));
      onSuccess();
      handleClose();
    } else {
      toast.error(res.error?.data?.message ?? t('something_went_wrong'));
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setReason(e.target.value);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="delete-inspection-modal"
      PaperProps={{
        style: {
          padding: 0,
          minWidth: 300,
        },
      }}
    >
      <DialogActions className="dialog-actions d-flex justify-content-between w-100">
        <DialogTitle className="p-1" color="inherit">
          {t('modal_title_cancel_inspection')}
        </DialogTitle>
        <IconButton className="close-btn" onClick={onClose}>
          <Close color="inherit" />
        </IconButton>
      </DialogActions>
      <FormControl className="p-4 d-flex flex-grow-1 flex-column">
        <TextField
          name={t('placeholder_reason_to_cancel')}
          multiline
          required
          value={reason}
          label={t('placeholder_reason_to_cancel')}
          maxRows={4}
          onChange={handleChange}
        />
        <Typography color="primary" className="text-center mt-4">
          {t('modal_body_are_you_sure')}
        </Typography>
        <footer className="p-0 mt-4 d-flex flex-row justify-content-between flex-grow-1 align-items-end">
          <Button variant="outlined" onClick={onClose}>
            {t('back')}
          </Button>
          <Button
            disabled={!reason.length}
            variant="contained"
            onClick={handleSubmit}
          >
            {t('btn_yes_cancel')}
          </Button>
        </footer>
      </FormControl>
    </Dialog>
  );
};
